import { useRef, useState } from 'react';
import AddressFields, { type AddressValues } from './AddressFields';
import type { Contact, Occasion, RelationshipType, CardStylePreference, CardType } from '../../../../shared/src/types/database';
import { apiFetch, ApiError } from '../../lib/api';

const MONTHS = [
  { value: '01', label: 'January' },  { value: '02', label: 'February' },
  { value: '03', label: 'March' },    { value: '04', label: 'April' },
  { value: '05', label: 'May' },      { value: '06', label: 'June' },
  { value: '07', label: 'July' },     { value: '08', label: 'August' },
  { value: '09', label: 'September' },{ value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

const ANNIVERSARY_RELATIONSHIPS: RelationshipType[] = ['spouse', 'husband', 'wife', 'partner'];

export interface ContactFormValues {
  name:                  string;
  relationship_type:     RelationshipType | '';
  address_street:        string;
  address_line2:         string;
  address_city:          string;
  address_state:         string;
  address_postal:        string;
  address_country:       string;
  personalization_notes: string;
  card_style_preference: CardStylePreference | '';
  card_type:             CardType;
}

export const EMPTY_CONTACT_FORM: ContactFormValues = {
  name:                  '',
  relationship_type:     '',
  address_street:        '',
  address_line2:         '',
  address_city:          '',
  address_state:         '',
  address_postal:        '',
  address_country:       'US',
  personalization_notes: '',
  card_style_preference: '',
  card_type:             'folded',
};

interface InitialBirthday {
  month:      string;
  day:        string;
  year:       string;
  recurring?: boolean;
}

interface InitialAnniversary {
  month: string;
  day:   string;
  year:  string;
}

interface ContactFormProps {
  /** Pre-fill when editing an existing contact */
  initial?:            Partial<ContactFormValues>;
  /** When provided, issues PUT instead of POST */
  contactId?:          string;
  /** Existing avatar URL to show as initial preview */
  initialAvatarUrl?:   string | null;
  /** Pre-fill birthday fields from an existing birthday occasion */
  initialBirthday?:    InitialBirthday | null;
  /** Pre-fill anniversary fields from an existing anniversary occasion */
  initialAnniversary?: InitialAnniversary | null;
  /** Pre-fill parent status from existing contact */
  initialIsParent?:    boolean;
  initialParentHoliday?: 'mothers_day' | 'fathers_day' | null;
  onSave:              (contact: Contact, autoOccasions?: Occasion[]) => void;
  onCancel?:           () => void;
  submitLabel?:        string;
  /** Called instead of showing an error when the contact limit is reached */
  onLimitReached?:     () => void;
}

const RELATIONSHIP_OPTIONS: { value: RelationshipType; label: string }[] = [
  { value: 'mom',         label: 'Mom'         },
  { value: 'dad',         label: 'Dad'         },
  { value: 'wife',        label: 'Wife'        },
  { value: 'husband',     label: 'Husband'     },
  { value: 'partner',     label: 'Partner'     },
  { value: 'child',       label: 'Child'       },
  { value: 'sibling',     label: 'Sibling'     },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'best_friend', label: 'Best Friend' },
  { value: 'friend',      label: 'Friend'      },
  { value: 'coworker',    label: 'Coworker'    },
  { value: 'other',       label: 'Other'       },
];

const STYLE_OPTIONS: { value: CardStylePreference; label: string }[] = [
  { value: 'sentimental', label: '🥹  Sentimental' },
  { value: 'funny',       label: '😄  Funny' },
  { value: 'elegant',     label: '✨  Elegant' },
  { value: 'floral',      label: '🌸  Floral' },
  { value: 'modern',      label: '🎨  Modern' },
  { value: 'religious',   label: '🙏  Religious' },
];

export default function ContactForm({
  initial,
  contactId,
  initialAvatarUrl,
  initialBirthday,
  initialAnniversary,
  initialIsParent,
  initialParentHoliday,
  onSave,
  onCancel,
  submitLabel = 'Save contact',
  onLimitReached,
}: ContactFormProps) {
  const [form, setForm] = useState<ContactFormValues>({
    ...EMPTY_CONTACT_FORM,
    ...initial,
  });
  const [saving,       setSaving      ] = useState(false);
  const [error,        setError       ] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Avatar
  const [avatarPreview, setAvatarPreview] = useState<string | null>(initialAvatarUrl ?? null);
  const [avatarFile,    setAvatarFile   ] = useState<File | null>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  // Birthday — pre-populated from existing occasion when editing
  const [bdayMonth,    setBdayMonth   ] = useState(initialBirthday?.month ?? '');
  const [bdayDay,      setBdayDay     ] = useState(initialBirthday?.day   ?? '');
  const [bdayYear,     setBdayYear    ] = useState(initialBirthday?.year  ?? '');
  const [bdayRecurring,setBdayRecurring] = useState(initialBirthday?.recurring !== false);
  // Anniversary (shown for partner-type relationships)
  const [showAnniversary,  setShowAnniversary ] = useState(!!(initialAnniversary?.month));
  const [annivMonth,       setAnnivMonth      ] = useState(initialAnniversary?.month ?? '');
  const [annivDay,         setAnnivDay        ] = useState(initialAnniversary?.day   ?? '');
  const [annivYear,        setAnnivYear       ] = useState(initialAnniversary?.year  ?? '');
  // Parent status
  const [isParent,         setIsParent        ] = useState(initialIsParent ?? false);
  const [parentHoliday,    setParentHoliday   ] = useState<'mothers_day' | 'fathers_day' | ''>(initialParentHoliday ?? '');

  const showAnnivPrompt = ANNIVERSARY_RELATIONSHIPS.includes(
    form.relationship_type as RelationshipType,
  );

  function patch(update: Partial<ContactFormValues>) {
    setForm((prev) => ({ ...prev, ...update }));
  }

  const addressValues: AddressValues = {
    address_street:  form.address_street,
    address_line2:   form.address_line2,
    address_city:    form.address_city,
    address_state:   form.address_state,
    address_postal:  form.address_postal,
    address_country: form.address_country,
  };

  function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Photo must be under 5 MB.');
      return;
    }
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }

    setSaving(true);
    try {
      const parsedBdayYear  = bdayYear  ? parseInt(bdayYear,  10) : NaN;
      const parsedAnnivYear = annivYear ? parseInt(annivYear, 10) : NaN;
      const currentYear = new Date().getFullYear();

      const payload = {
        name:                  form.name.trim(),
        relationship_type:     form.relationship_type     || undefined,
        address_street:        form.address_street        || undefined,
        address_line2:         form.address_line2         || undefined,
        address_city:          form.address_city          || undefined,
        address_state:         form.address_state         || undefined,
        address_postal:        form.address_postal        || undefined,
        address_country:       form.address_country,
        personalization_notes: form.personalization_notes || undefined,
        card_style_preference: form.card_style_preference || undefined,
        card_type:             form.card_type,
        // Birthday
        birthday_month:     bdayMonth  || undefined,
        birthday_day:       bdayDay    || undefined,
        birthday_year:      (!isNaN(parsedBdayYear) && parsedBdayYear > 1900 && parsedBdayYear <= currentYear) ? parsedBdayYear : undefined,
        birthday_recurring: (bdayMonth && bdayDay) ? bdayRecurring : undefined,
        // Anniversary (only when toggle is on and fields filled)
        anniversary_month: (showAnnivPrompt && showAnniversary && annivMonth) ? annivMonth : undefined,
        anniversary_day:   (showAnnivPrompt && showAnniversary && annivDay)   ? annivDay   : undefined,
        anniversary_year:  (showAnnivPrompt && showAnniversary && !isNaN(parsedAnnivYear) && parsedAnnivYear > 1900 && parsedAnnivYear <= currentYear) ? parsedAnnivYear : undefined,
        // Parent status — mom/dad relationships imply is_parent on the backend
        is_parent:      isParent || form.relationship_type === 'mom' || form.relationship_type === 'dad',
        parent_holiday: isParent && form.relationship_type !== 'mom' && form.relationship_type !== 'dad'
          ? (parentHoliday || undefined)
          : undefined,
      };

      const json = await apiFetch<{ contact: Contact; auto_occasions?: Occasion[] }>(
        contactId ? `/api/contacts/${contactId}` : '/api/contacts',
        { method: contactId ? 'PUT' : 'POST', body: JSON.stringify(payload) },
      );

      let finalContact = json.contact;

      // Upload avatar if a new file was selected
      if (avatarFile) {
        const reader = new FileReader();
        const base64: string = await new Promise((resolve) => {
          reader.onload = (ev) => resolve(ev.target?.result as string);
          reader.readAsDataURL(avatarFile);
        });
        try {
          const avatarJson = await apiFetch<{ avatar_url: string }>(
            `/api/contacts/${finalContact.id}/avatar`,
            {
              method: 'POST',
              body: JSON.stringify({ data: base64, contentType: avatarFile.type }),
            },
          );
          finalContact = { ...finalContact, avatar_url: avatarJson.avatar_url };
        } catch {
          // Avatar upload failure is non-fatal — contact is still saved
        }
      }

      onSave(finalContact, json.auto_occasions ?? []);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'CONTACT_LIMIT_REACHED' && onLimitReached) {
        onLimitReached();
        return;
      }
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  }

  const initials = form.name.trim().charAt(0).toUpperCase() || '?';

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>

      {/* Avatar */}
      <div className="flex flex-col items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => avatarInputRef.current?.click()}
          className="relative group"
          aria-label="Upload photo"
        >
          <div className="h-20 w-20 rounded-full overflow-hidden bg-sage/15 flex items-center justify-center ring-2 ring-white shadow-warm">
            {avatarPreview ? (
              <img src={avatarPreview} alt="Avatar preview" className="h-full w-full object-cover" />
            ) : (
              <span className="font-heading text-2xl text-sage font-semibold">{initials}</span>
            )}
          </div>
          {/* Camera overlay */}
          <div className="absolute inset-0 rounded-full bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        </button>
        <p className="font-body text-xs text-gray-400">
          {avatarPreview ? 'Click to change photo' : 'Add a photo (optional)'}
        </p>
        <input
          ref={avatarInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleAvatarChange}
        />
      </div>

      {/* Name */}
      <div>
        <label className="block font-body text-sm font-medium text-navy mb-1.5">
          Full name <span className="text-rose" style={{ color: '#c0534a' }}>*</span>
        </label>
        <input
          type="text"
          placeholder="e.g. Mom, Sarah Johnson, Grandpa Lou"
          value={form.name}
          onChange={(e) => patch({ name: e.target.value })}
          disabled={saving}
          required
          autoFocus
          className="input-field w-full"
        />
        <p className="mt-1 text-xs text-gray-400 font-body">
          Use whatever name feels natural, this is just for your reference.
        </p>
      </div>

      {/* Relationship */}
      <div>
        <label className="block font-body text-sm font-medium text-navy mb-1.5">
          Relationship
        </label>
        <select
          value={form.relationship_type}
          onChange={(e) => patch({ relationship_type: e.target.value as RelationshipType | '' })}
          disabled={saving}
          className="input-field w-full"
        >
          <option value="">Select one…</option>
          {RELATIONSHIP_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Is a parent — hidden for mom/dad (implied) */}
      {form.relationship_type !== 'mom' && form.relationship_type !== 'dad' && (
        <div>
          <div className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
            <div>
              <p className="font-body text-sm font-medium text-navy">Is a parent?</p>
              <p className="font-body text-xs text-gray-400 mt-0.5">
                {isParent ? "We'll add a Mother's Day or Father's Day occasion" : "Let us know so we can remind you"}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isParent}
              onClick={() => { setIsParent(!isParent); if (isParent) setParentHoliday(''); }}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${isParent ? 'bg-sage' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${isParent ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          {isParent && (
            <div className="mt-2 flex gap-2">
              {(['mothers_day', 'fathers_day'] as const).map((val) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setParentHoliday(val)}
                  disabled={saving}
                  className={`flex-1 rounded-xl px-3 py-2.5 text-xs font-body border transition-all ${
                    parentHoliday === val
                      ? 'bg-sage/10 border-sage text-sage font-medium'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-sage/50'
                  }`}
                >
                  {val === 'mothers_day' ? "🌸 Mother's Day" : "👔 Father's Day"}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Birthday */}
      <div>
        <label className="block font-body text-sm font-medium text-navy mb-1.5">
          Birthday <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <div className="flex gap-2">
          <select
            value={bdayMonth}
            onChange={(e) => setBdayMonth(e.target.value)}
            disabled={saving}
            className="input-field flex-1"
          >
            <option value="">Month</option>
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <select
            value={bdayDay}
            onChange={(e) => setBdayDay(e.target.value)}
            disabled={saving}
            className="input-field w-24"
          >
            <option value="">Day</option>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
              <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
            ))}
          </select>
        </div>
        <p className="mt-1 text-xs text-gray-400 font-body">
          Month and day is all we need for annual reminders.
        </p>
        {(bdayMonth || bdayDay) && (
          <div className="mt-2 flex flex-col gap-2">
            <input
              type="number"
              placeholder="Birth year (optional, e.g. 1985)"
              value={bdayYear}
              onChange={(e) => setBdayYear(e.target.value)}
              min={1900}
              max={new Date().getFullYear()}
              disabled={saving}
              className="input-field w-full"
            />
            <p className="text-xs text-gray-400 font-body">
              Only needed for milestone birthday alerts (40th, 50th, etc.)
            </p>
          </div>
        )}
        {bdayMonth && bdayDay && (
          <div className="mt-2 flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
            <div>
              <p className="font-body text-sm font-medium text-navy">Send every year</p>
              <p className="font-body text-xs text-gray-400 mt-0.5">
                {bdayRecurring ? "We'll send a birthday card each year" : 'One card only'}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={bdayRecurring}
              onClick={() => setBdayRecurring(!bdayRecurring)}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${bdayRecurring ? 'bg-sage' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${bdayRecurring ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        )}
      </div>

      {/* Anniversary — only for partner-type relationships */}
      {showAnnivPrompt && (
        <div>
          <div className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
            <div>
              <p className="font-body text-sm font-medium text-navy">Add anniversary date?</p>
              <p className="font-body text-xs text-gray-400 mt-0.5">
                {showAnniversary ? "We'll send a card on your anniversary each year" : 'Skip for now'}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={showAnniversary}
              onClick={() => setShowAnniversary(!showAnniversary)}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                showAnniversary ? 'bg-sage' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                showAnniversary ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
          {showAnniversary && (
            <div className="mt-2 flex flex-col gap-2">
              <div className="flex gap-2">
                <select
                  value={annivMonth}
                  onChange={(e) => setAnnivMonth(e.target.value)}
                  disabled={saving}
                  className="input-field flex-1"
                >
                  <option value="">Month</option>
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <select
                  value={annivDay}
                  onChange={(e) => setAnnivDay(e.target.value)}
                  disabled={saving}
                  className="input-field w-24"
                >
                  <option value="">Day</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
                  ))}
                </select>
              </div>
              <input
                type="number"
                placeholder="Year (optional, e.g. 2019)"
                value={annivYear}
                onChange={(e) => setAnnivYear(e.target.value)}
                min={1900}
                max={new Date().getFullYear()}
                disabled={saving}
                className="input-field w-full"
              />
              <p className="text-xs text-gray-400 font-body">
                We'll send a card every year on your anniversary.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Card format: single premium product (2026 folded-card pivot).
          No longer a choice: every card is a folded card mailed in an envelope.
          Shown as an informational callout so the customer knows what they get. */}
      <div>
        <label className="block font-body text-sm font-medium text-navy mb-1.5">
          Card
        </label>
        <div className="rounded-xl px-4 py-3 border border-sage/40 bg-sage/5">
          <p className="font-body text-sm font-medium text-navy">
            💌 A real folded card, mailed in an envelope
          </p>
          <p className="font-body text-xs text-gray-500 mt-0.5">
            Printed on premium cardstock and mailed for you. Cards in your plan's
            yearly allowance are included; any beyond it are $5.99 each.
          </p>
        </div>
      </div>

      {/* Address */}
      <div>
        <p className="font-body text-sm font-medium text-navy mb-1.5">
          Mailing address
        </p>
        <p className="font-body text-xs text-gray-400 mb-3">
          Where should we mail their cards? We'll verify it's deliverable.
        </p>
        <AddressFields
          values={addressValues}
          onChange={(update) => patch(update as Partial<ContactFormValues>)}
          disabled={saving}
        />
      </div>

      {/* Advanced — card style + personalization notes */}
      <button
        type="button"
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-1.5 text-xs font-body text-sage hover:text-sage/80 transition-colors self-start"
      >
        <svg
          className={`h-3.5 w-3.5 transition-transform ${showAdvanced ? 'rotate-90' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        {showAdvanced ? 'Hide' : 'Add'} card preferences (optional)
      </button>

      {showAdvanced && (
        <div className="flex flex-col gap-4 pl-4 border-l-2 border-sage/20">

          {/* Card style preference */}
          <div>
            <label className="block font-body text-sm font-medium text-navy mb-2">
              Card style they'd love
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {STYLE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => patch({
                    card_style_preference: form.card_style_preference === opt.value ? '' : opt.value,
                  })}
                  className={`text-left rounded-xl px-3 py-2.5 text-xs font-body border transition-all ${
                    form.card_style_preference === opt.value
                      ? 'bg-sage/10 border-sage text-sage font-medium'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-sage/50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>


        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-sm font-body text-center" style={{ color: '#c0534a' }}>{error}</p>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-1">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="btn-secondary flex-1"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={saving || !form.name.trim()}
          className="btn-primary flex-1 disabled:opacity-60"
        >
          {saving ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
